const STEP_SECONDS = 1 / 60;
const STEP_MS = 1000 / 60;
const MAX_DELTA_MS = 100;
const MAX_CATCH_UP_STEPS = 5;
const EPSILON_MS = 1e-7;

export interface FixedStepFrame {
  steps: number;
  alpha: number;
}

export class FixedStepClock {
  private lastTimestampMs: number | null = null;
  private accumulatorMs = 0;
  private droppedMs = 0;

  get droppedTimeMs(): number {
    return this.droppedMs;
  }

  reset(): void {
    this.lastTimestampMs = null;
  }

  consume(timestampMs: number, step: (stepSeconds: number) => void): FixedStepFrame {
    if (!Number.isFinite(timestampMs)) {
      return { steps: 0, alpha: this.alpha() };
    }

    if (this.lastTimestampMs === null) {
      this.lastTimestampMs = timestampMs;
      return { steps: 0, alpha: this.alpha() };
    }

    const rawDeltaMs = Math.max(0, timestampMs - this.lastTimestampMs);
    this.lastTimestampMs = timestampMs;
    const deltaMs = Math.min(MAX_DELTA_MS, rawDeltaMs);
    if (rawDeltaMs > deltaMs) this.droppedMs += rawDeltaMs - deltaMs;
    this.accumulatorMs += deltaMs;

    let steps = 0;
    while (
      this.accumulatorMs + EPSILON_MS >= STEP_MS &&
      steps < MAX_CATCH_UP_STEPS
    ) {
      step(STEP_SECONDS);
      this.accumulatorMs -= STEP_MS;
      if (this.accumulatorMs < 0 && this.accumulatorMs > -EPSILON_MS) {
        this.accumulatorMs = 0;
      }
      steps += 1;
    }

    if (this.accumulatorMs + EPSILON_MS >= STEP_MS) {
      const wholeStepsToDrop = Math.floor((this.accumulatorMs + EPSILON_MS) / STEP_MS);
      const dropped = wholeStepsToDrop * STEP_MS;
      this.accumulatorMs -= dropped;
      this.droppedMs += dropped;
    }

    return { steps, alpha: this.alpha() };
  }

  private alpha(): number {
    return Math.min(0.999999, Math.max(0, this.accumulatorMs / STEP_MS));
  }
}
